import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { CoolIcon } from "@/components/ui/cool-icon";
import type { ReactNode } from "react";

/**
 * Compact compound-tracker preview for the overview page. Links out to
 * /compound for the full tracker once real extraction data exists. Until
 * then it reads as an honest "nothing extracted yet" card — matching the
 * /compound page's own zero-state rather than fabricating numbers.
 */

export interface CompoundPreviewProps {
  extracted?: number;
  reinvested?: number;
  nextUnlock?: number;
}

function fmtMoney(n: number | undefined): string {
  if (n == null) return "—";
  return `$${n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function CompoundPreview({
  extracted,
  reinvested,
  nextUnlock,
}: CompoundPreviewProps): ReactNode {
  const isEmpty = extracted == null && reinvested == null && nextUnlock == null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border/40 bg-card p-4 font-sans sm:p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <CoolIcon name="Trending_Up" />
          </div>
          <h3 className="text-base font-semibold tracking-tight">
            Compound Tracker
          </h3>
        </div>
        <Link
          to="/compound"
          className="text-xs font-medium text-accent hover:underline"
        >
          View Full Tracker →
        </Link>
      </div>

      {isEmpty ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Lights Up After Your First Extraction. The Tracker Plots Your Batch
          Funnel And Reinvestment Ratio Once A Real Payout Lands.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
          <Tile label="Extracted" value={fmtMoney(extracted)} accent="emerald" />
          <Tile label="Reinvested" value={fmtMoney(reinvested)} accent="pink" />
          <Tile label="Next Unlock" value={fmtMoney(nextUnlock)} accent="muted" />
        </div>
      )}
    </motion.div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "emerald" | "pink" | "muted";
}): ReactNode {
  const color =
    accent === "emerald"
      ? "text-emerald-500"
      : accent === "pink"
        ? "text-accent"
        : "text-foreground";
  return (
    <div className="min-w-0 rounded-xl bg-muted/40 px-2 py-3 text-center sm:px-3">
      <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground sm:text-[11px]">
        {label}
      </p>
      <p className={`mt-1 truncate font-mono text-base font-semibold tabular-nums sm:text-lg ${color}`}>
        {value}
      </p>
    </div>
  );
}
